/**
 * The board of a master account scheduled for deletion: only cancelling the deletion and the master account's billing
 * stay reachable, mirroring the account's pending-deletion gate. Billing renders with a banner;
 * every other board page is covered by a non-dismissable gate.
 */
import { useMutation, useQuery } from '@apollo/client/react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Icon, Modal } from '@insio/ui-toolkit';
import { formatDotTimestamp } from '@insio/dates';
import { AccountStatus, MasterAccountPermission } from '@insio/shared-types';
import { CancelMasterAccountDeletionDocument, GetMasterAccountDocument } from '@/graphql';
import { useMasterPermissions, useSignOut, useSwitchAccount } from '@/features/auth';
import { navigation } from '@/navigation';
import { useMasterAccountBoardErrorMessage } from '@/hooks/use-master-account-board-error';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';

/** No-op handler for the non-dismissable modal */
const noop = () => {};

/** CSS class to hide the close button rendered by Modal.Header */
const HIDE_CLOSE_BTN = '[&_[role=button]]:hidden!';

interface MasterAccountPendingDeletionGateProps {
  children: ReactNode;
}

export function MasterAccountPendingDeletionGate({
  children
}: MasterAccountPendingDeletionGateProps) {
  const t = useFeatureTranslation('masterAccountBoardShell.pendingDeletion');
  const location = useLocation();
  const navigate = useNavigate();
  const { hasMasterPermission } = useMasterPermissions();
  const { signOut, loading: signingOut } = useSignOut();
  const { targets, activeTargetId, switchTo, switching } = useSwitchAccount();
  const { showError } = useToastHelpers();
  const masterAccountBoardError = useMasterAccountBoardErrorMessage();
  const { data } = useQuery(GetMasterAccountDocument);
  // The returned status lands in the cache and lifts the gate.
  const [cancelDeletion, { loading: cancelling }] = useMutation(
    CancelMasterAccountDeletionDocument
  );

  const master = data?.masterAccount;
  if (master?.status !== AccountStatus.PENDING_DELETION) {
    return <>{children}</>;
  }

  const canCancel = hasMasterPermission(MasterAccountPermission.SETTINGS_MANAGE);
  const canManageBilling = hasMasterPermission(MasterAccountPermission.BILLING_MANAGE);
  const onBilling = location.pathname.startsWith(navigation.masterAccountBilling());
  const scheduledLabel = master.deletionScheduledAt
    ? formatDotTimestamp(master.deletionScheduledAt)
    : '';
  const busy = cancelling || signingOut || switching;

  const handleCancel = async () => {
    try {
      const { error } = await cancelDeletion();
      // errorPolicy 'all' resolves a GraphQL refusal instead of throwing it.
      if (error) throw error;
    } catch (e) {
      showError(masterAccountBoardError(e) ?? t('cancelError'));
    }
  };

  const cancelButton = canCancel && (
    <Button
      variant='primary'
      size={onBilling ? 'sm' : 'full'}
      disabled={busy}
      testId='master-pending-deletion-cancel'
      onClick={() => void handleCancel()}
    >
      {t('cancel')}
    </Button>
  );

  if (onBilling && canManageBilling) {
    return (
      <div className='flex h-full min-h-0 flex-col'>
        <div
          role='status'
          data-testid='master-pending-deletion-banner'
          className='flex flex-col gap-sm border-b border-warn-border bg-warn-bg px-md py-sm sm:flex-row sm:items-center sm:justify-between'
        >
          <div className='flex items-start gap-sm'>
            <Icon name='triangle-exclamation' className='mt-2xs shrink-0 text-warn-text' />
            <span className='text-sm text-warn-text'>
              {t('banner', { name: master.name, date: scheduledLabel })}
            </span>
          </div>
          {cancelButton}
        </div>
        <div className='min-h-0 flex-1'>{children}</div>
      </div>
    );
  }

  // A master account being deleted must not strand its people: other master accounts and own organizations stay
  // reachable, as in the account's gate.
  const otherPlaces = targets.filter(target => target.id !== activeTargetId);

  return (
    <Modal open onClose={noop} size='sm' layout='vertical' testId='master-pending-deletion-gate'>
      <Modal.Header onClose={noop} className={HIDE_CLOSE_BTN}>
        <span className='text-lg font-medium'>{t('title')}</span>
      </Modal.Header>

      <Modal.Content className='flex flex-col gap-lg p-lg sm:p-xl'>
        <p className='text-sm text-foreground-muted'>
          {t('description', { name: master.name, date: scheduledLabel })}
        </p>
        {!canCancel && <p className='text-sm text-foreground-muted'>{t('askAdmin')}</p>}

        <div className='flex flex-col gap-sm'>
          {cancelButton}

          {canManageBilling && (
            <Button
              variant='secondary'
              size='full'
              disabled={busy}
              onClick={() => navigate(navigation.masterAccountBilling())}
            >
              {t('billing')}
            </Button>
          )}

          {otherPlaces.length > 0 && (
            <div className='flex flex-col gap-xs'>
              <span className='text-sm text-foreground-muted'>{t('switchHint')}</span>
              {otherPlaces.map(place => (
                <Button
                  key={place.id}
                  variant='secondary'
                  size='full'
                  disabled={busy}
                  onClick={() => void switchTo(place.id)}
                >
                  {t('switchTo', { name: place.name })}
                </Button>
              ))}
            </div>
          )}

          <Button variant='ghost' size='full' disabled={busy} onClick={signOut}>
            {t('logout')}
          </Button>
        </div>
      </Modal.Content>
    </Modal>
  );
}
